import * as React from 'react';
import axios from 'axios';
import moment from 'moment';
import { Table } from 'react-bootstrap';
import sanitizeHtml from 'sanitize-html';
import styled from 'styled-components';

import { theme } from 'components';
import { TYPE_FILTER_MAP } from 'components/FroalaTaggedInput';
import Loading from '../../react-common/Loading';

export type HistoryTableProps = {
  id?: string;
  type: 'OfferSettings' | 'AppointmentConfig' | 'Offer' | 'User';
};

export type HistoryTableState = {
  history: any[];
  isLoading: boolean;
};

class HistoryTable extends React.Component<
  HistoryTableProps,
  HistoryTableState
> {
  constructor(props: HistoryTableProps) {
    super(props);
    this.state = {
      history: [],
      isLoading: true,
    };
  }

  getHistory = async () => {
    const { id, type } = this.props;
    const { data } = await axios.get(`/history/${type}/${id}`);
    let history = data
      .reduce((acc: object[], historyObj: any) => {
        const copy = { ...historyObj };
        if (historyObj.diffs.length > 0) {
          copy.diffs[0].count = copy.diffs.length;
          acc.push(...copy.diffs);
        }
        return acc;
      }, [])
      .sort(
        (a: any, b: any) =>
          moment(b.updatedAt).valueOf() - moment(a.updatedAt).valueOf(),
      );

    // APP-319 Extra step for offer settings new Template Manager
    if (type === 'OfferSettings') {
      const parseHtml = (value: string) => {
        const replacedTags = TYPE_FILTER_MAP.offer.reduce((acc, cur) => acc.replace(
            new RegExp(`{{${cur}}}`, 'gi'),
            `<span class="template-tag">${cur}</span>`,
          ), value);
        return sanitizeHtml(replacedTags as string, {
          allowedTags: sanitizeHtml.defaults.allowedTags.concat([
            'img',
            'span',
            'wbr',
          ]),
          allowedAttributes: false,
        });
      };
      history = history.map((h: any) => {
        if (h.field === 'Notification Message') {
          return {
            ...h,
            from: parseHtml(h.from),
            to: parseHtml(h.to),
          };
        }
        return h;
      });
    }

    this.setState({ history, isLoading: false });
  };

  async componentDidMount() {
    const { id } = this.props;
    if (id) await this.getHistory();
  }

  render() {
    const { history, isLoading } = this.state;
    const { id, type } = this.props;
    if (!id || !history.length)
      return (
        <h5 style={{ textAlign: 'center' }}>
          No {type.split(/(?=[A-Z])/).join(' ')} history recorded.
        </h5>
      );
    return isLoading ? (
      <Loading size="fa-3x" />
    ) : (
      <Table bordered>
        <thead>
          <tr>
            <th>Date</th>
            <th>Name</th>
            <th>Changed</th>
            <th>From</th>
            <th>To</th>
          </tr>
        </thead>
        <tbody>
          {history.map((h, i) => (
            <tr key={i}>
              {h.count && (
                <>
                  <td rowSpan={h.count}>{moment(h.updatedAt).format('lll')}</td>
                  <td rowSpan={h.count}>
                    {h.user.firstName} {h.user.lastName}
                  </td>
                </>
              )}
              <td>{h.field}</td>
              {type === 'OfferSettings' &&
              h.field === 'Notification Message' ? (
                <TableDefWithTag dangerouslySetInnerHTML={{ __html: h.from }} />
              ) : (
                <TableDefWithTag>{h.from}</TableDefWithTag>
              )}
              {type === 'OfferSettings' &&
              h.field === 'Notification Message' ? (
                <TableDefWithTag dangerouslySetInnerHTML={{ __html: h.to }} />
              ) : (
                <TableDefWithTag>{h.to}</TableDefWithTag>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }
}

export default HistoryTable;

const TableDefWithTag = styled.td`
  && {
    word-break: break-word;
  }

  .template-tag {
    padding: 1px 4px;
    margin: 0px;

    color: ${theme.geekblue6};
    font-size: 12px;
    line-height: 20px;

    background-color: ${theme.geekblue1};
    opacity: 0.85;

    border: 1px solid ${theme.geekblue3};
    border-radius: 4px;
  }
`;
